import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GenerateFileDownloadInfoResponse,
  ListTaxDocumentsOptions,
  ListTaxDocumentsResponse,
  TaxDocument,
  generateFileDownloadInfo as universalGenerateFileDownloadInfo,
  getTaxDocument as universalGetTaxDocument,
  listTaxDocuments as universalListTaxDocuments,
} from './payments-tax-documents-v1-tax-document-tax-documents.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function getTaxDocument(
  httpClient: HttpClient
): GetTaxDocumentSignature {
  return (taxDocumentId: string) =>
    universalGetTaxDocument(
      taxDocumentId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTaxDocumentSignature {
  /**
   * Retrieves a tax document by ID.
   * @param - ID of the tax document to retrieve.
   * @returns Retrieved tax document.
   */
  (taxDocumentId: string): Promise<
    NonNullablePaths<
      TaxDocument,
      | `revision`
      | `accountId`
      | `type`
      | `period.yearPeriod.year`
      | `downloadable`
      | `problematic`
      | `sourceType`,
      4
    >
  >;
}

export function listTaxDocuments(
  httpClient: HttpClient
): ListTaxDocumentsSignature {
  return (accountId: string, options?: ListTaxDocumentsOptions) =>
    universalListTaxDocuments(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListTaxDocumentsSignature {
  /**
   * Retrieves a list of tax documents that belong to a Wix Payments account.
   *
   * You must specify the `accountId` of the account whose tax documents you want to retrieve.
   *
   * Tax documents are sorted in descending order by `period`, then by `createdDate`.
   *
   * When `type` is omitted, tax documents of all types are returned.
   * @param - ID of the Wix Payments account whose tax documents are retrieved.
   */
  (accountId: string, options?: ListTaxDocumentsOptions): Promise<
    NonNullablePaths<
      ListTaxDocumentsResponse,
      | `taxDocuments`
      | `taxDocuments.${number}.revision`
      | `taxDocuments.${number}.accountId`
      | `taxDocuments.${number}.type`
      | `taxDocuments.${number}.period.yearPeriod.year`
      | `taxDocuments.${number}.downloadable`
      | `taxDocuments.${number}.problematic`
      | `taxDocuments.${number}.sourceType`,
      6
    >
  >;
}

export function generateFileDownloadInfo(
  httpClient: HttpClient
): GenerateFileDownloadInfoSignature {
  return (taxDocumentId: string) =>
    universalGenerateFileDownloadInfo(
      taxDocumentId,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateFileDownloadInfoSignature {
  /**
   * Generates temporary information for downloading a tax document's file.
   *
   * The returned `downloadInfo.url` is a temporary link to a PDF. It expires 10 seconds after it's generated, so follow it immediately, and don't store it as a static link. Call this method again to get a fresh URL.
   *
   * The tax document must have a file available, meaning its `downloadable` field is `true`.
   * @param - ID of the tax document to download a file for.
   */
  (taxDocumentId: string): Promise<GenerateFileDownloadInfoResponse>;
}

export {
  AccountInfo,
  ActionEvent,
  ConfirmFileUploadedRequest,
  ConfirmFileUploadedResponse,
  CursorPaging,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  FileUploadTarget,
  GenerateFileDownloadInfoRequest,
  GenerateFileDownloadInfoResponse,
  GenerateFileUploadTargetRequest,
  GenerateFileUploadTargetResponse,
  GenerateHistoryFileDownloadInfoRequest,
  GenerateHistoryFileDownloadInfoResponse,
  GetTaxDocumentFileHistoryRequest,
  GetTaxDocumentFileHistoryResponse,
  GetTaxDocumentRequest,
  GetTaxDocumentResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListTaxDocumentsOptions,
  ListTaxDocumentsRequest,
  ListTaxDocumentsResponse,
  MessageEnvelope,
  PagingMetadataV2,
  RefreshTaxDocumentFileRequest,
  RefreshTaxDocumentFileResponse,
  RestoreInfo,
  SourceType,
  TaxDocument,
  TaxDocumentFileHistory,
  TaxDocumentFileHistoryRecord,
  TaxDocumentType,
  TaxPeriod,
  TaxPeriodOptionsOneOf,
  UpdateTaxDocumentRequest,
  UpdateTaxDocumentResponse,
  WebhookIdentityType,
  YearPeriod,
} from './payments-tax-documents-v1-tax-document-tax-documents.universal.js';
